import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import HubPortalContext from "./HubPortalContext";
import HubCreateWizard from "./views/HubCreateWizard";
import HubLobbyView from "./views/HubLobbyView";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  Bird, Plus, ArrowRight, Loader2, Users, Zap, Copy, Check,
  LayoutDashboard, Truck, HardHat, Signal, ChevronRight, LogOut, RefreshCw,
  Wrench, ArrowRightLeft, Eye, Sparkles, Lock, ArrowLeft, X, PlusCircle,
  Fuel, Droplets, Layers, Package, ClipboardCheck, Trash2, Bookmark, BookmarkCheck,
  QrCode, Share2, Download, Shield
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import HUB_TYPES, { getAvailableTypes, ROLE_CONFIGS } from "@/lib/hubTypes";
import LanguageSwitcher from "@/components/shared/LanguageSwitcher";

const ROLE_CONFIG = {
  planner: { icon: LayoutDashboard, color: "bg-lime-500", textColor: "text-slate-950", label: "Planner", mandatory: true },
  driver: { icon: Truck, color: "bg-red-600", textColor: "text-white", label: "Driver", mandatory: true },
  machinist: { icon: HardHat, color: "bg-amber-500", textColor: "text-slate-950", label: "Machinist", mandatory: true },
  service: { icon: Wrench, color: "bg-purple-600", textColor: "text-white", label: "Service", mandatory: false },
  transport: { icon: ArrowRightLeft, color: "bg-blue-600", textColor: "text-white", label: "Transport", mandatory: false },
  viewer: { icon: Eye, color: "bg-slate-600", textColor: "text-white", label: "Viewer", mandatory: false },
};

export default function HubPortal() {
  const { auth, setAuth, session, setSession } = useAppContext();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const joinInvite = searchParams.get("invite") || "";

  const [view, setView] = useState(joinInvite ? "join-preview" : "list");
  const [myHubs, setMyHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinInfo, setJoinInfo] = useState(null);
  const [joinDisplayName, setJoinDisplayName] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);

  // Create hub state
  const [hubName, setHubName] = useState("");
  const [hubDesc, setHubDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedAssetType, setSelectedAssetType] = useState("");
  const [selectedRoleConfig, setSelectedRoleConfig] = useState("full");
  const [customItems, setCustomItems] = useState({ a: "", b: "", c: "" });
  const [customUnits, setCustomUnits] = useState({ a: "", b: "", c: "" });
  const [itemBEnabled, setItemBEnabled] = useState(true);
  const [itemCEnabled, setItemCEnabled] = useState(false);
  const [customActions, setCustomActions] = useState([]);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Hub lobby state
  const [activeHub, setActiveHub] = useState(null);
  const [hubStatus, setHubStatus] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteRole, setInviteRole] = useState("driver");
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [copied, setCopied] = useState("");
  const [expandedQR, setExpandedQR] = useState(null);

  // Demo hub state
  const [showDemo, setShowDemo] = useState(false);
  const [demoRole, setDemoRole] = useState("planner");
  const [seedingDemo, setSeedingDemo] = useState(false);

  const jwt = auth?.jwt;

  const loadMyHubs = useCallback(async () => {
    if (!jwt) return;
    setLoading(true);
    try {
      const hubs = await api.getMyHubs(jwt);
      setMyHubs(hubs);
    } catch (e) { void 0; }
    setLoading(false);
  }, [jwt]);

  const loadTemplates = useCallback(async () => {
    if (!jwt) return;
    try {
      const tpls = await api.getTemplates(jwt);
      setTemplates(tpls);
    } catch (e) { void 0; }
  }, [jwt]);

  useEffect(() => {
    if (joinInvite && jwt) {
      loadInviteInfo(joinInvite);
    } else {
      loadMyHubs();
      loadTemplates();
    }
  }, [jwt, joinInvite, loadMyHubs, loadTemplates]);

  const loadInviteInfo = async (token) => {
    try {
      const info = await api.getInviteInfo(token);
      setJoinInfo(info);
      setJoinDisplayName(auth?.user?.email?.split("@")[0] || "");
      setLoading(false);
    } catch (e) {
      void 0;
      setView("list");
      loadMyHubs();
      loadTemplates();
    }
  };

  const handleConfirmJoin = async () => {
    if (!joinInvite || !jwt) return;
    setJoinLoading(true);
    try {
      const result = await api.joinHub(jwt, { invite_token: joinInvite, display_name: joinDisplayName || auth?.user?.email?.split("@")[0] || "" });
      const member = result.member;
      const hub = result.hub;
      const role = member?.role || joinInfo?.role || "driver";
      const displayName = member?.display_name || joinDisplayName || auth?.user?.email?.split("@")[0] || role;
      const sess = {
        hubId: hub?.id || joinInfo?.hub_id,
        hubName: hub?.name || joinInfo?.hub_name || "",
        role,
        lang: "en",
        driverName: displayName,
        userName: displayName,
        asset_type: hub?.asset_type || joinInfo?.asset_config?.asset_type || "refueling",
        asset_config: hub?.asset_config || joinInfo?.asset_config || {},
      };
      setSession(sess);
      if (role === "planner") nav("/planner");
      else if (role === "driver") nav(`/driver/${displayName}`);
      else if (role === "machinist") nav("/machinist");
      else if (role === "service") nav("/service");
      else if (role === "transport") nav("/transport");
      else nav("/viewer");
    } catch (e) {
      void 0;
      setView("list");
      await loadMyHubs();
    }
    setJoinLoading(false);
  };

  const handleCreateHub = async (e) => {
    e.preventDefault();
    if (!hubName.trim() || !selectedAssetType) return;
    setCreating(true);
    try {
      const config = HUB_TYPES[selectedAssetType] || HUB_TYPES.refueling;
      const roleConf = ROLE_CONFIGS.find(r => r.id === selectedRoleConfig) || ROLE_CONFIGS[0];
      const result = await api.createHubAuth(jwt, {
        name: hubName,
        description: hubDesc,
        asset_type: selectedAssetType,
        asset_config: {
          item_a_label: customItems.a || config.item_a.label,
          item_a_unit: customUnits.a || config.item_a.unit,
          item_b_label: customItems.b || config.item_b.label,
          item_b_unit: customUnits.b || config.item_b.unit,
          item_b_enabled: itemBEnabled && config.item_b.enabled !== false,
          item_c_label: customItems.c || config.item_c?.label || "",
          item_c_unit: customUnits.c || config.item_c?.unit || "",
          item_c_enabled: itemCEnabled,
          level_label: config.level.label,
          request_label: config.request_label,
          delivery_label: config.delivery_label,
          request_type_primary: config.request_type_primary,
          role_config: selectedRoleConfig,
          min_occupancy: roleConf.min_occupancy,
          actions: config.is_task_based ? customActions : [],
        },
      });
      setActiveHub(result.hub);
      await loadHubStatus(result.hub.id);
      // Save as template if requested
      if (saveAsTemplate && templateName.trim()) {
        try {
          await api.saveTemplate(jwt, {
            name: templateName.trim(),
            asset_type: selectedAssetType,
            asset_config: result.hub.asset_config,
          });
          await loadTemplates();
        } catch (e) { void 0; }
      }
      setView("lobby");
      setHubName(""); setHubDesc(""); setSelectedAssetType(""); setWizardStep(1);
      setCustomItems({ a: "", b: "", c: "" }); setCustomUnits({ a: "", b: "", c: "" });
      setItemBEnabled(true); setItemCEnabled(false); setCustomActions([]);
      setSelectedRoleConfig("full");
      setSaveAsTemplate(false); setTemplateName("");
    } catch (e) { void 0; }
    setCreating(false);
  };

  const loadHubStatus = async (hubId) => {
    try {
      const status = await api.getHubStatus(hubId);
      setHubStatus(status);
      const invs = await api.getInvites(jwt, hubId);
      setInvites(invs);
    } catch (e) { void 0; }
  };

  const applyTemplate = (tpl) => {
    const ac = tpl.asset_config || {};
    setSelectedAssetType(tpl.asset_type);
    const cfg = HUB_TYPES[tpl.asset_type] || HUB_TYPES.refueling;
    setCustomItems({ a: ac.item_a_label || cfg.item_a.label, b: ac.item_b_label || cfg.item_b.label, c: ac.item_c_label || "" });
    setCustomUnits({ a: ac.item_a_unit || cfg.item_a.unit, b: ac.item_b_unit || cfg.item_b.unit, c: ac.item_c_unit || "" });
    setItemBEnabled(ac.item_b_enabled !== undefined ? ac.item_b_enabled : cfg.item_b.enabled !== false);
    setItemCEnabled(ac.item_c_enabled || false);
    setSelectedRoleConfig(ac.role_config || "full");
    setCustomActions(ac.actions || []);
    if (cfg.is_task_based && (!ac.actions || ac.actions.length === 0)) setCustomActions([""]);
    setWizardStep(2);
  };

  const handleDeleteTemplate = async (tplId) => {
    try {
      await api.deleteTemplate(jwt, tplId);
      setTemplates(prev => prev.filter(t => t.id !== tplId));
    } catch (e) { void 0; }
  };

  const handleDeleteHub = async (hubId) => {
    try {
      await api.deleteHub(jwt, hubId);
      setDeleteConfirmId(null);
      await loadMyHubs();
    } catch (e) { void 0; }
  };

  const handleEnterHub = async (membership) => {
    const hubId = membership.hub_id;
    const role = membership.role;
    const sess = {
      hubId,
      hubName: membership.hub_name || hubId,
      role,
      lang: "en",
      driverName: membership.display_name || role,
      userName: membership.display_name || auth?.user?.email?.split("@")[0] || role,
      asset_type: membership.asset_type || "refueling",
      asset_config: membership.asset_config || {},
    };
    // Auto-start trial on first hub entry (fire-and-forget)
    try { await api.startTrial(jwt); } catch {}
    setSession(sess);
    if (role === "planner") nav("/planner");
    else if (role === "driver") nav(`/driver/${membership.display_name || ""}`);
    else if (role === "machinist") nav("/machinist");
    else if (role === "service") nav("/service");
    else if (role === "transport") nav("/transport");
    else nav("/viewer");
  };

  const handleOpenLobby = async (membership) => {
    const hubId = membership.hub_id;
    const hub = { id: hubId, name: membership.hub_name, status: membership.hub_status };
    setActiveHub(hub);
    await loadHubStatus(hubId);
    setView("lobby");
  };

  const handleCreateInvite = async () => {
    if (!activeHub) return;
    try {
      await api.createInvite(jwt, activeHub.id, { role: inviteRole, display_name: inviteName, email: inviteEmail });
      await loadHubStatus(activeHub.id);
      setInviteName("");
      setInviteEmail("");
    } catch (e) { void 0; }
  };

  const copyInviteLink = async (token) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/join/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(token);
    } catch {
      const el = document.getElementById(`invite-link-${token}`);
      if (el) { el.select(); document.execCommand("copy"); setCopied(token); }
    }
    setTimeout(() => setCopied(""), 3000);
  };

  const qrToBlob = (token) => {
    return new Promise((resolve) => {
      const svg = document.getElementById(`qr-svg-${token}`);
      if (!svg) return resolve(null);
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      canvas.width = 512; canvas.height = 600;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 512, 600);
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 56, 24, 400, 400);
        ctx.fillStyle = "#0f172a";
        ctx.font = "bold 16px Inter, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(activeHub?.name || "Pigeon Supply Hub", 256, 460);
        ctx.fillStyle = "#64748b";
        ctx.font = "13px Inter, sans-serif";
        const inv = invites.find(i => i.token === token);
        ctx.fillText(`Join as ${ROLE_CONFIG[inv?.role]?.label || "Member"}`, 256, 484);
        ctx.fillStyle = "#84cc16";
        ctx.fillRect(156, 500, 200, 3);
        ctx.fillStyle = "#94a3b8";
        ctx.font = "11px Inter, sans-serif";
        ctx.fillText("Pigeon Supply", 256, 530);
        canvas.toBlob(resolve, "image/png");
      };
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    });
  };

  const shareInvite = async (token) => {
    const link = `${window.location.origin}/auth?invite=${token}`;
    const inv = invites.find(i => i.token === token);
    const roleName = ROLE_CONFIG[inv?.role]?.label || "Member";
    const hubName = activeHub?.name || "Hub";
    const text = `Join ${hubName} as ${roleName} on Pigeon Supply`;

    if (navigator.share) {
      try {
        const blob = await qrToBlob(token);
        const shareData = { title: text, text: `${text}\n${link}`, url: link };
        if (blob && navigator.canShare?.({ files: [new File([blob], "invite-qr.png", { type: "image/png" })] })) {
          shareData.files = [new File([blob], `pigeon-invite-${roleName.toLowerCase()}.png`, { type: "image/png" })];
        }
        await navigator.share(shareData);
      } catch (e) {
        if (e.name !== "AbortError") copyInviteLink(token);
      }
    } else {
      copyInviteLink(token);
    }
  };

  const downloadQR = async (token) => {
    const blob = await qrToBlob(token);
    if (!blob) return;
    const inv = invites.find(i => i.token === token);
    const roleName = ROLE_CONFIG[inv?.role]?.label || "member";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pigeon-invite-${roleName.toLowerCase()}-${token}.png`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleEnterDemoHub = async () => {
    setSeedingDemo(true);
    try {
      await api.seedDemoHub();
      const sess = {
        hubId: "DEMO-TRAINING-HUB",
        hubName: "Demo & Training Hub",
        role: demoRole,
        lang: "en",
        driverName: `Demo ${ROLE_CONFIG[demoRole]?.label || "User"}`,
        userName: `Demo ${ROLE_CONFIG[demoRole]?.label || "User"}`,
      };
      setSession(sess);
      if (demoRole === "planner") nav("/planner");
      else if (demoRole === "driver") nav("/driver/Demo Driver");
      else if (demoRole === "machinist") nav("/machinist");
      else if (demoRole === "service") nav("/service");
      else if (demoRole === "transport") nav("/transport");
      else nav("/viewer");
    } catch (e) { void 0; }
    setSeedingDemo(false);
  };

  const handleLogout = () => {
    setAuth(null);
    setSession(null);
    localStorage.removeItem("pigeon-auth");
    localStorage.removeItem("pigeon-session");
    nav("/auth");
  };

  const portalCtx = {
    t, jwt, auth, session, setSession, nav,
    view, setView, myHubs, loadMyHubs,
    wizardStep, setWizardStep, selectedAssetType, setSelectedAssetType,
    customItems, setCustomItems, customUnits, setCustomUnits,
    itemBEnabled, setItemBEnabled, itemCEnabled, setItemCEnabled,
    customActions, setCustomActions, selectedRoleConfig, setSelectedRoleConfig,
    hubName, setHubName, hubDesc, setHubDesc,
    saveAsTemplate, setSaveAsTemplate, templateName, setTemplateName,
    creating, handleCreateHub, templates, handleDeleteTemplate, applyTemplate,
    activeHub, hubStatus, invites, inviteRole, setInviteRole,
    inviteName, setInviteName, inviteEmail, setInviteEmail, handleCreateInvite, expandedQR, setExpandedQR,
    shareInvite, downloadQR, copyInviteLink, copied,
    handleEnterHub, loadHubStatus,
  };

  return (
    <HubPortalContext.Provider value={portalCtx}>
    <div className="min-h-[100dvh] bg-white flex flex-col relative overflow-hidden" data-testid="hub-portal">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(101,163,13,0.04),transparent_50%)]" />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 py-5 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center shadow-md">
            <Bird className="w-5 h-5 text-slate-950" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">Pigeon Supply</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{auth?.user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher compact />
          {view === "lobby" && (
            <button onClick={() => { setView("list"); loadMyHubs(); }} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-xs font-bold border border-slate-200" data-testid="back-to-hubs-btn">
              Back
            </button>
          )}
          <button onClick={() => nav("/privacy")} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-xs font-bold border border-slate-200 flex items-center gap-2" data-testid="privacy-settings-btn">
            <Shield className="w-3.5 h-3.5" /> {t("privacy_btn")}
          </button>
          <button onClick={handleLogout} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-xs font-bold border border-slate-200 flex items-center gap-2" data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> {t("sign_out")}
          </button>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center p-6">
        {/* HUB LIST */}
        {view === "list" && (
          <div className="w-full max-w-xl" data-testid="hub-list-view">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase text-slate-900 mb-2">{t("my_hubs")}</h2>
              <p className="text-sm text-slate-400 font-medium">{t("your_logistics_hubs")}</p>
            </div>

            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-lime-500 animate-spin" /></div>
            ) : (
              <>
                {myHubs.length > 0 && (
                  <div className="grid gap-3 mb-6">
                    {myHubs.map((m) => {
                      const rc = ROLE_CONFIG[m.role] || ROLE_CONFIG.viewer;
                      const Icon = rc.icon;
                      const isDeployed = m.hub_status === "DEPLOYED";
                      return (
                        <div key={m.id} className="relative group" data-testid={`hub-card-${m.hub_id}`}>
                          {deleteConfirmId === m.hub_id ? (
                            <div className="p-5 bg-red-50 border border-red-300 rounded-xl animate-fadeIn" data-testid={`delete-confirm-${m.hub_id}`}>
                              <p className="text-sm font-bold text-red-600 mb-1">{t("delete")} "{m.hub_name || m.hub_id}"?</p>
                              <p className="text-[10px] text-red-400 mb-3">{t("delete_confirm_msg")}</p>
                              <div className="flex gap-2">
                                <button data-testid={`confirm-delete-${m.hub_id}`} onClick={() => handleDeleteHub(m.hub_id)}
                                  className="flex-1 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-bold uppercase active:scale-95 transition-all">
                                  {t("delete_forever")}
                                </button>
                                <button data-testid={`cancel-delete-${m.hub_id}`} onClick={() => setDeleteConfirmId(null)}
                                  className="flex-1 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-lg text-xs font-bold uppercase">
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                data-testid={`hub-entry-${m.hub_id}`}
                                onClick={() => isDeployed ? handleEnterHub(m) : handleOpenLobby(m)}
                                className="flex-1 p-5 bg-slate-50 border border-slate-200 rounded-xl hover:border-lime-400 transition-all text-left flex items-center justify-between active:scale-[0.98]"
                              >
                                <div className="flex items-center gap-4">
                                  <div className={`w-12 h-12 ${rc.color} rounded-xl flex items-center justify-center ${rc.textColor}`}>
                                    <Icon className="w-6 h-6" strokeWidth={2} />
                                  </div>
                                  <div>
                                    <h3 className="text-base font-bold text-slate-900 tracking-tight">{m.hub_name || m.hub_id}</h3>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{t("role_" + m.role) || rc.label}</span>
                                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDeployed ? "bg-lime-100 text-lime-600" : "bg-amber-100 text-amber-400"}`}>
                                        {isDeployed ? t("deployed_status").toUpperCase() : t("waiting_label").toUpperCase()}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-lime-500 transition-all" />
                              </button>
                              {isDeployed && m.role === "planner" && (
                                <button data-testid={`manage-hub-${m.hub_id}`} onClick={() => handleOpenLobby(m)}
                                  className="p-3 text-slate-400 hover:text-lime-500 opacity-0 group-hover:opacity-100 transition-all rounded-xl hover:bg-lime-50"
                                  title="Manage & Invites">
                                  <Share2 className="w-4.5 h-4.5" />
                                </button>
                              )}
                              {m.role === "planner" && (
                              <button data-testid={`delete-hub-${m.hub_id}`} onClick={() => setDeleteConfirmId(m.hub_id)}
                                className="p-3 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all rounded-xl hover:bg-red-50">
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  data-testid="create-hub-btn"
                  onClick={() => setView("create")}
                  className="w-full p-6 border-2 border-dashed border-slate-200 hover:border-lime-400 rounded-xl flex items-center justify-center gap-3 text-slate-400 hover:text-lime-600 transition-all group"
                >
                  <Plus className="w-6 h-6 group-hover:rotate-90 transition-transform" />
                  <span className="text-sm font-bold uppercase tracking-wider">{t("create_hub")}</span>
                </button>

                {/* Demo Hub Section */}
                <button
                  data-testid="try-demo-hub-btn"
                  onClick={() => setShowDemo(true)}
                  className="w-full mt-3 p-5 bg-amber-50 border border-amber-200 hover:border-amber-400 rounded-xl flex items-center justify-center gap-3 text-amber-400 hover:text-amber-300 transition-all group"
                >
                  <Sparkles className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-bold uppercase tracking-wider">{t("try_demo_hub")}</span>
                </button>

                {/* Demo Role Picker Modal */}
                {showDemo && (
                  <div className="mt-4 p-5 bg-slate-50 border border-amber-300 rounded-xl animate-fadeIn" data-testid="demo-role-picker">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 text-center">{t("choose_role_explore")}</h3>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {Object.entries(ROLE_CONFIG).map(([role, rc]) => {
                        const Icon = rc.icon;
                        return (
                          <button
                            key={role}
                            data-testid={`demo-role-${role}`}
                            onClick={() => setDemoRole(role)}
                            className={`p-3 rounded-xl text-center transition-all border ${
                              demoRole === role
                                ? "bg-amber-50 border-amber-400 shadow-sm"
                                : "bg-slate-50/80 border-slate-200 hover:border-slate-200"
                            }`}
                          >
                            <div className={`w-8 h-8 ${demoRole === role ? rc.color : "bg-slate-100"} rounded-xl flex items-center justify-center mx-auto mb-2 ${demoRole === role ? rc.textColor : "text-slate-400"}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <p className={`text-[10px] font-bold uppercase tracking-tight ${demoRole === role ? "text-amber-400" : "text-slate-400"}`}>{t("role_" + role) || rc.label}</p>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setShowDemo(false)} className="flex-1 py-3 bg-slate-100 text-slate-400 rounded-xl text-xs font-bold">{t("cancel")}</button>
                      <button
                        data-testid="enter-demo-hub-btn"
                        onClick={handleEnterDemoHub}
                        disabled={seedingDemo}
                        className="flex-[2] py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 text-slate-950 rounded-xl text-xs font-black uppercase flex items-center justify-center gap-2 shadow-md"
                      >
                        {seedingDemo ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> {t("enter_as")} {t("role_" + demoRole) || ROLE_CONFIG[demoRole]?.label}</>}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* CREATE HUB */}
        {view === "create" && <HubCreateWizard />}

        {/* HUB LOBBY */}
        {view === "lobby" && activeHub && <HubLobbyView />}
        {/* JOIN PREVIEW */}
        {view === "join-preview" && (
          <div className="w-full max-w-md" data-testid="join-preview-view">
            {!joinInfo ? (
              <div className="text-center">
                <Loader2 className="w-10 h-10 text-lime-500 animate-spin mx-auto mb-4" />
                <p className="text-slate-400 font-medium">{t("loading_invite")}</p>
              </div>
            ) : joinInfo.used ? (
              <div className="text-center animate-fadeInUp">
                <div className="w-16 h-16 bg-slate-100 border border-slate-200 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <X className="w-8 h-8 text-slate-400" />
                </div>
                <h2 className="text-2xl font-black tracking-tighter uppercase text-slate-900 mb-2">{t("invite_used")}</h2>
                <p className="text-sm text-slate-400 font-medium mb-6">{t("invite_used_desc")}</p>
                <button onClick={() => { setView("list"); loadMyHubs(); nav("/portal"); }}
                  className="px-6 py-3 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-sm font-black uppercase" data-testid="go-to-portal-btn">
                  {t("go_to_hubs")}
                </button>
              </div>
            ) : (
              <div className="animate-fadeInUp">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-lime-50 border border-lime-300 rounded-xl flex items-center justify-center mx-auto mb-5">
                    <Users className="w-8 h-8 text-lime-500" />
                  </div>
                  <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-2">{t("youre_invited")}</h2>
                  <p className="text-sm text-slate-400 font-medium">{t("join_the_crew")}</p>
                </div>

                {/* Hub Info Card */}
                <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl mb-5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">{t("hub_details")}</p>
                  <h3 className="text-xl font-black text-slate-900 tracking-tight mb-2" data-testid="join-hub-name">{joinInfo.hub_name}</h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-[10px] font-bold px-2 py-1 bg-slate-100 text-slate-500 rounded-full uppercase">{joinInfo.hub_type}</span>
                    <span className="text-[10px] font-bold px-2 py-1 bg-slate-100 text-slate-500 rounded-full">{joinInfo.current_members} member{joinInfo.current_members !== 1 ? "s" : ""}</span>
                  </div>
                </div>

                {/* Role Badge — BIG */}
                {(() => {
                  const rc = ROLE_CONFIG[joinInfo.role] || ROLE_CONFIG.viewer;
                  const Icon = rc.icon;
                  return (
                    <div className={`p-6 ${rc.color} rounded-xl mb-5 flex items-center gap-4`} data-testid="join-role-badge">
                      <div className={`w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center ${rc.textColor}`}>
                        <Icon className="w-7 h-7" strokeWidth={2} />
                      </div>
                      <div>
                        <p className={`text-xs font-bold uppercase tracking-widest ${rc.textColor} opacity-80`}>{t("your_role")}</p>
                        <p className={`text-2xl font-black uppercase tracking-tight ${rc.textColor}`}>{t("role_" + joinInfo.role) || rc.label}</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Display Name Input */}
                <div className="mb-5">
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{t("display_name")}</label>
                  <input
                    data-testid="join-display-name-input"
                    value={joinDisplayName}
                    onChange={(e) => setJoinDisplayName(e.target.value)}
                    placeholder={t("how_crew_sees_you")}
                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold placeholder:text-slate-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/20 outline-none transition-all"
                  />
                </div>

                {/* Join Button */}
                <button
                  data-testid="confirm-join-btn"
                  onClick={handleConfirmJoin}
                  disabled={joinLoading}
                  className={`w-full py-5 ${ROLE_CONFIG[joinInfo.role]?.color || "bg-lime-500"} hover:opacity-90 ${ROLE_CONFIG[joinInfo.role]?.textColor || "text-slate-950"} rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 shadow-lg transition-all active:scale-[0.98]`}
                >
                  {joinLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {t("join_as")} {t("role_" + joinInfo.role) || ROLE_CONFIG[joinInfo.role]?.label || "Member"} <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <p className="text-center text-[11px] text-slate-400 mt-4 font-medium">
                  {t("added_to_hub")} <span className="font-bold text-slate-500">{joinInfo.hub_name}</span> {t("as_a_role")} <span className="font-bold text-slate-500">{t("role_" + joinInfo.role) || ROLE_CONFIG[joinInfo.role]?.label}</span>.
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
    </HubPortalContext.Provider>
  );
}
